import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';
import { Input } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { IConvertLeadsSuccessModalProps } from '@mm/state/modules/modalsState/props';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CheckIcon } from '../../../../../../../common/component-icons/CheckIcon/CheckIcon';
import { CloseModalProps } from '../../../../../../../common/modals/modal-helpers';
import { colors } from '../../../../styles/colors';
import { useAudienceNameValidation } from '../../../LeadsContacts/use-audience-name-validation';
import { LeadsCreationPendingInfoAlert } from '../LeadsCreationPendingInfoAlert/LeadsCreationPendingInfoAlert';
import styles from './CongratsLeadsPurchaseModal.module.css';

export const CongratsLeadsPurchaseModal = ({
  convertedLeads,
  onClose,
  onNext,
  closeModal,
}: IConvertLeadsSuccessModalProps & CloseModalProps) => {
  const purchasedContactsCount = convertedLeads?.leadIds.length
    ? convertedLeads?.leadIds.length
    : Number(convertedLeads?.searchBody.limit) -
      Number(convertedLeads?.excludedLeadIds.length);
  const leadText = purchasedContactsCount === 1 ? 'lead' : 'leads';

  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const [newAudienceName, setNewAudienceName] = useState(
    convertedLeads?.audience.name,
  );
  const trimmedNewAudienceName = newAudienceName?.trim();
  const debouncedNewAudienceName = useDebounce(trimmedNewAudienceName, 700);
  const { validateAudienceName } = useAudienceNameValidation();
  const [valid, setValid] = useState(!!trimmedNewAudienceName);
  const [checking, setChecking] = useState(false);

  const renameAudience = async () => {
    if (newAudienceName !== convertedLeads?.audience.name) {
      await audiencesCRUDActions.update(
        { ...convertedLeads?.audience, name: trimmedNewAudienceName },
        activeChatbotId,
      );
    }
  };

  useEffect(() => {
    (async () => {
      if (
        debouncedNewAudienceName &&
        debouncedNewAudienceName !== convertedLeads?.audience.name
      ) {
        setChecking(true);
        const valid = await validateAudienceName(debouncedNewAudienceName);
        setValid(valid);
        setChecking(false);
      }
    })();
  }, [
    debouncedNewAudienceName,
    validateAudienceName,
    convertedLeads?.audience.name,
  ]);

  const onClickDone = () => {
    renameAudience();
    closeModal();
    onNext();
  };

  const onCloseModal = async () => {
    renameAudience();
    await onClose?.();
    closeModal();
  };

  return (
    <div className={styles.container}>
      <CloseOutlined className={styles.closeIcon} onClick={onCloseModal} />

      <div className={styles.header}>
        <CheckCircleFilled className={styles.filledCircle} />
        <div className={styles.title}>
          Congratulations!
          <div className={styles.subtitle}>
            Meet your new {purchasedContactsCount} {leadText}
          </div>
        </div>
      </div>

      <LeadsCreationPendingInfoAlert
        title="Your new leads are being added to your dashboard!"
        description="This process may take up to 15 minutes."
      />

      {convertedLeads?.audience && (
        <div className={styles.audienceNameWrapper}>
          Name Your New Audience
          <Input
            css={{
              input: styles.audienceEditBarInput,
              container: styles.audienceEditBar,
            }}
            value={newAudienceName}
            onChange={e => setNewAudienceName(e.target.value)}
            invalid={!valid && !checking}
            isLoading={checking}
            icon={
              valid && !checking && trimmedNewAudienceName ? (
                <CheckIcon color={colors.green} size={16} />
              ) : undefined
            }
            placeholder="Enter name here..."
          />
          {!valid && !checking && (
            <div className={styles.error}>
              An audience with that name already exists
            </div>
          )}
        </div>
      )}

      <div className={styles.buttons}>
        <PrimaryButton
          onClick={onClickDone}
          className={styles.primaryBtn}
          disabled={!valid}
        >
          <ArrowRightOutlined className={styles.arrow} />
          Next step
        </PrimaryButton>
      </div>
    </div>
  );
};
